import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { SUBSCRIPTION_LIMITS, PREMIUM_FEATURES } from '@/types/subscription';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { Crown, CheckCircle2, Loader2, Tag, Sparkles, Zap, TrendingUp, Shield, Clock, AlertCircle, CreditCard, Copy, Mail, RefreshCw, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import confetti from 'canvas-confetti';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CouponCode {
  id: string;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  applicable_plans: string[];
  current_uses: number;
  max_uses?: number;
  valid_until?: string;
}

const PAYPAL_PLAN_IDS = {
  yearly: import.meta.env.VITE_PAYPAL_YEARLY_PLAN_ID || ''
};

// PayPal-Umgebung bestimmen
const getPayPalEnvironment = () => {
  const clientId = import.meta.env.VITE_PAYPAL_CLIENT_ID || '';
  const isProduction = import.meta.env.VITE_PAYPAL_ENVIRONMENT === 'production';
  
  // Sandbox Client IDs beginnen normalerweise mit 'sb' oder enthalten 'sandbox'
  const isSandboxClientId = clientId.startsWith('sb') || clientId.includes('sandbox');
  
  return {
    environment: isProduction && !isSandboxClientId ? 'production' : 'sandbox',
    clientId,
    isConfigured: !!clientId
  };
};

const isPayPalConfigured = () => {
  const config = getPayPalEnvironment();
  return config.isConfigured;
};

export function UpgradeModal({ isOpen, onClose }: UpgradeModalProps) {
  const { user } = useAuth();
  const { subscription, fetchSubscription } = useSubscription();
  const [{ isPending, isInitial, isLoading, isResolved, isRejected }, dispatch] = usePayPalScriptReducer();
  const [isProcessing, setIsProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<CouponCode | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [showBankTransferModal, setShowBankTransferModal] = useState(false);
  
  // Neue States für verbesserte Zahlungsverifikation
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [paymentVerificationFailed, setPaymentVerificationFailed] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [lastOrderId, setLastOrderId] = useState<string | null>(null);
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [isManualVerifying, setIsManualVerifying] = useState(false);
  
  // Stripe State
  const [isStripeProcessing, setIsStripeProcessing] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'paypal' | 'stripe' | 'bank'>('paypal');
  
  // Gutscheincode ausklappbar
  const [showCouponField, setShowCouponField] = useState(false);

  // Timeout für PayPal-Loading (falls es zu lange dauert)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isPending) {
        setShowFallback(true);
      }
    }, 10000); // 10 Sekunden Timeout

    return () => clearTimeout(timer);
  }, [isPending]);

  // Reset fallback when modal closes
  useEffect(() => {
    if (!isOpen) {
      setShowFallback(false);
      setIsVerifyingPayment(false);
      setPaymentVerificationFailed(false);
      setLastOrderId(null);
      setVerificationAttempts(0);
    }
  }, [isOpen]);

  // Gutscheincode-Validierung
  useEffect(() => {
    const validateCouponRealtime = async () => {
      // Reset state wenn kein Code oder User
      if (!couponCode.trim()) {
        setAppliedCoupon(null);
        setCouponError(null);
        setIsValidatingCoupon(false);
        return;
      }

      if (!user) {
        setAppliedCoupon(null);
        setCouponError(null);
        setIsValidatingCoupon(false);
        return;
      }

      // Nur validieren wenn wir tatsächlich einen Code haben
      if (couponCode.trim().length < 3) {
        setAppliedCoupon(null);
        setCouponError(null);
        setIsValidatingCoupon(false);
        return;
      }

      setIsValidatingCoupon(true);

      try {
        const { data: couponData, error } = await supabase
          .from('coupon_codes')
          .select('*')
          .eq('code', couponCode.toUpperCase())
          .eq('active', true)
          .maybeSingle();

        if (error || !couponData) {
          setCouponError('Gutscheincode nicht gefunden');
          setAppliedCoupon(null);
          setIsValidatingCoupon(false);
          return;
        }

        // Prüfe Gültigkeit
        if (couponData.valid_until && new Date(couponData.valid_until) < new Date()) {
          setCouponError('Gutscheincode ist abgelaufen');
          setAppliedCoupon(null);
          setIsValidatingCoupon(false);
          return;
        }

        // Prüfe Nutzungslimit
        if (couponData.max_uses && couponData.current_uses >= couponData.max_uses) {
          setCouponError('Gutscheincode bereits vollständig genutzt');
          setAppliedCoupon(null);
          setIsValidatingCoupon(false);
          return;
        }

        // Prüfe ob User den Code bereits verwendet hat
        const { data: usageData } = await supabase
          .from('coupon_usage')
          .select('id')
          .eq('user_id', user.id)
          .eq('coupon_id', couponData.id)
          .maybeSingle();

        if (usageData) {
          setCouponError('Gutscheincode bereits verwendet');
          setAppliedCoupon(null);
          setIsValidatingCoupon(false);
          return;
        }

        // Prüfe ob Code für Jahresplan anwendbar ist
        if (!couponData.applicable_plans.includes('yearly')) {
          setCouponError('Gutscheincode nicht für Jahresplan gültig');
          setAppliedCoupon(null);
          setIsValidatingCoupon(false);
          return;
        }

        // Gutscheincode ist gültig
        setAppliedCoupon(couponData);
        setCouponError(null);
        setIsValidatingCoupon(false);
      } catch (error) {
        console.error('Error validating coupon:', error);
        setCouponError('Fehler bei der Validierung');
        setAppliedCoupon(null);
        setIsValidatingCoupon(false);
      }
    };

    const debounceTimer = setTimeout(validateCouponRealtime, 1000); // Noch längere Debounce-Zeit
    return () => clearTimeout(debounceTimer);
  }, [couponCode, user]);

  // Preis berechnen mit Gutschein - memoized um Re-Renders zu vermeiden
  const finalYearlyPrice = useMemo(() => {
    if (!appliedCoupon || !appliedCoupon.applicable_plans.includes('yearly')) return 16.90;

    if (appliedCoupon.discount_type === 'percentage') {
      return Math.max(0, 16.90 - (16.90 * appliedCoupon.discount_value / 100));
    } else {
      return Math.max(0, 16.90 - appliedCoupon.discount_value);
    }
  }, [appliedCoupon]);

  // Stabile PayPal-Konfiguration
  const paypalConfig = useMemo(() => ({
    amount: finalYearlyPrice.toFixed(2),
    currency: 'EUR',
    description: appliedCoupon 
      ? `KaufPause Premium Jährlich (mit Gutschein: ${appliedCoupon.code})`
      : 'KaufPause Premium Jährlich'
  }), [finalYearlyPrice, appliedCoupon?.code]);

  // Stabile Callbacks
  const handlePayPalCreateOrder = useCallback((data, actions) => {
    console.log('PayPal createOrder called, user:', user?.id ? 'authenticated' : 'not authenticated');
    
    // Debug PayPal-Konfiguration
    const paypalEnv = getPayPalEnvironment();
    console.log('🔧 PayPal Environment:', {
      environment: paypalEnv.environment,
      clientId: paypalEnv.clientId ? `${paypalEnv.clientId.substring(0, 10)}...` : 'NOT_SET',
      isConfigured: paypalEnv.isConfigured
    });
    
    if (!user?.id) {
      console.error('User not authenticated during PayPal order creation');
      toast.error('Bitte melde dich an, um fortzufahren');
      setShowFallback(true);
      throw new Error('Benutzer nicht angemeldet');
    }
    
    console.log('💰 Creating PayPal order with config:', paypalConfig);
    
    return actions.order.create({
      purchase_units: [{
        amount: {
          value: paypalConfig.amount,
          currency_code: paypalConfig.currency
        },
        description: paypalConfig.description,
        custom_id: user.id // User ID für Webhook-Verarbeitung
      }],
      intent: 'CAPTURE',
      application_context: {
        shipping_preference: 'NO_SHIPPING'
      }
    });
  }, [paypalConfig, user?.id]);

  // Verbesserte automatische Zahlungsverifikation mit mehreren Retry-Versuchen
  const verifyPayment = async (orderId: string, retryCount = 0) => {
    const maxRetries = 5; // Erhöht von 3 auf 5
    const retryDelay = 3000; // Erhöht von 2000 auf 3000ms
    
    try {
      console.log(`Verifying payment for order ${orderId}, attempt ${retryCount + 1}/${maxRetries}`);
      
      // Mehrere Suchstrategien für bessere Trefferquote
      const searchStrategies = [
        // 1. Suche nach PayPal Order ID
        () => supabase
          .from('subscriptions')
          .select('*')
          .eq('paypal_subscription_id', orderId)
          .eq('tier', 'yearly')
          .eq('status', 'active')
          .maybeSingle(),
        
        // 2. Suche nach Payment ID
        () => supabase
          .from('subscriptions')
          .select('*')
          .eq('payment_id', orderId)
          .eq('tier', 'yearly')
          .eq('status', 'active')
          .maybeSingle(),
        
        // 3. Suche nach User ID und aktuellem Zeitraum
        () => supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user?.id)
          .eq('tier', 'yearly')
          .eq('status', 'active')
          .gte('updated_at', new Date(Date.now() - 10 * 60 * 1000).toISOString()) // Letzte 10 Minuten
          .maybeSingle()
      ];
      
      for (const strategy of searchStrategies) {
        const { data: subscription, error } = await strategy();
        
        if (!error && subscription) {
          console.log('Payment verification successful:', subscription);
          await fetchSubscription();
          
          // Erfolgsmeldung mit Konfetti
          const colors = ['#10B981', '#059669', '#34D399', '#6EE7B7'];
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 },
            colors: colors,
          });
          
          setTimeout(() => {
            confetti({
              particleCount: 50,
              angle: 60,
              spread: 55,
              origin: { x: 0, y: 0.6 },
              colors: colors,
            });
            confetti({
              particleCount: 50,
              angle: 120,
              spread: 55,
              origin: { x: 1, y: 0.6 },
              colors: colors,
            });
          }, 250);
          
          toast.success('🎉 Willkommen bei KaufPause Premium!', {
            description: 'Deine Zahlung war erfolgreich. Du hast jetzt Zugang zu allen Premium-Features!',
            duration: 8000,
          });
          
          setIsVerifyingPayment(false);
          setPaymentVerificationFailed(false);
          setVerificationAttempts(0);
          onClose();
          return true;
        }
      }
      
      // Wenn keine Subscription gefunden wurde, retry falls möglich
      if (retryCount < maxRetries - 1) {
        console.log(`Payment not found, retrying in ${retryDelay}ms...`);
        setVerificationAttempts(retryCount + 1);
        
        setTimeout(() => {
          verifyPayment(orderId, retryCount + 1);
        }, retryDelay);
        
        return false;
      } else {
        // Alle Versuche erschöpft
        console.log('Payment verification failed after all retries');
        setPaymentVerificationFailed(true);
        setVerificationAttempts(maxRetries);
        
        toast.warning('Automatische Verifikation fehlgeschlagen', {
          description: 'Deine Zahlung wurde möglicherweise verarbeitet. Verwende den manuellen Verifikations-Button.',
          duration: 10000,
        });
        
        return false;
      }
      
    } catch (error) {
      console.error('Error during payment verification:', error);
      
      if (retryCount < maxRetries - 1) {
        setTimeout(() => {
          verifyPayment(orderId, retryCount + 1);
        }, retryDelay);
        return false;
      } else {
        setPaymentVerificationFailed(true);
        toast.error('Fehler bei der Zahlungsverifikation');
        return false;
      }
    }
  };

  // Manuelle Zahlungsverifikation
  const handleManualVerification = async () => {
    if (!lastOrderId || !user?.id) {
      toast.error('Keine Order-ID verfügbar für die Verifikation');
      return;
    }
    
    setIsManualVerifying(true);
    
    try {
      console.log('Starting manual payment verification for order:', lastOrderId);
      
      // Erweiterte Suche mit verschiedenen Strategien
      const searchQueries = [
        // Suche nach PayPal Order ID
        supabase
          .from('subscriptions')
          .select('*')
          .eq('paypal_subscription_id', lastOrderId)
          .eq('user_id', user.id),
        
        // Suche nach Payment ID
        supabase
          .from('subscriptions')
          .select('*')
          .eq('payment_id', lastOrderId)
          .eq('user_id', user.id),
        
        // Suche nach kürzlich aktualisierten Subscriptions für diesen User
        supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .eq('tier', 'yearly')
          .eq('status', 'active')
          .gte('updated_at', new Date(Date.now() - 30 * 60 * 1000).toISOString()) // Letzte 30 Minuten
      ];
      
      for (const query of searchQueries) {
        const { data: subscriptions, error } = await query;
        
        if (!error && subscriptions && subscriptions.length > 0) {
          console.log('Found subscription via manual verification:', subscriptions[0]);
          
          await fetchSubscription();
          
          toast.success('✅ Zahlung erfolgreich verifiziert!', {
            description: 'Dein Premium-Account wurde aktiviert.',
            duration: 6000,
          });
          
          setIsManualVerifying(false);
          setPaymentVerificationFailed(false);
          setIsVerifyingPayment(false);
          onClose();
          return;
        }
      }
      
      // Wenn immer noch nichts gefunden wurde, prüfe Webhook-Logs
      const { data: webhookLogs } = await supabase
        .from('webhook_logs')
        .select('*')
        .ilike('payload', `%${lastOrderId}%`)
        .order('created_at', { ascending: false })
        .limit(5);
      
      if (webhookLogs && webhookLogs.length > 0) {
        console.log('Found webhook logs for order:', webhookLogs);
        
        toast.info('Zahlung wird noch verarbeitet', {
          description: 'Deine Zahlung wurde empfangen und wird verarbeitet. Bitte warte einen Moment.',
          duration: 8000,
        });
      } else {
        // Keine Subscription und keine Webhook-Logs gefunden
        toast.warning('Zahlung noch nicht verarbeitet', {
          description: 'Deine Zahlung wurde möglicherweise noch nicht vollständig verarbeitet. Bitte kontaktiere den Support falls das Problem weiterhin besteht.',
          duration: 10000,
        });
        
        setShowContactForm(true);
      }
      
    } catch (error) {
      console.error('Error during manual verification:', error);
      toast.error('Fehler bei der manuellen Verifikation');
    } finally {
      setIsManualVerifying(false);
    }
  };

  const handlePayPalApprove = useCallback(async (data, actions) => {
    console.log('PayPal approve called, data:', data);
    
    if (!user?.id) {
      console.error('User not authenticated during PayPal approval');
      toast.error('Benutzer nicht angemeldet. Bitte melde dich erneut an.');
      setShowFallback(true);
      return;
    }
    
    try {
      setIsProcessing(true);
      
      // Speichere Order ID für Verifikation
      setLastOrderId(data.orderID);
      
      console.log('PayPal payment approved, Order ID:', data.orderID);
      
      // Backend PayPal Capture - Vermeidet CORS-Probleme
      try {
        console.log('🔄 Starting backend PayPal capture for order:', data.orderID);
        
        // Hole aktuelles Auth-Token
        const { data: sessionData } = await supabase.auth.getSession();
        const authToken = sessionData.session?.access_token;
        
        if (!authToken) {
          throw new Error('No auth token available');
        }
        
        const captureResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/paypal-capture`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
          },
          body: JSON.stringify({
            orderID: data.orderID,
            userID: user.id,
            amount: finalYearlyPrice,
            currency: 'EUR',
            couponCode: appliedCoupon?.code
          })
        });

        if (!captureResponse.ok) {
          const errorText = await captureResponse.text();
          throw new Error(`Backend capture failed: ${captureResponse.status} - ${errorText}`);
        }

        const captureResult = await captureResponse.json();
        console.log('✅ Backend PayPal capture successful:', captureResult);
        
        // Bei erfolgreichem Backend-Capture ist die Zahlung bereits verarbeitet
        const actualPaidAmount = appliedCoupon 
          ? (appliedCoupon.discount_type === 'percentage' 
              ? finalYearlyPrice * (1 - appliedCoupon.discount_value / 100)
              : finalYearlyPrice - appliedCoupon.discount_value)
          : finalYearlyPrice;
        
        toast.success('💰 Zahlung erfolgreich abgebucht!', {
          description: `${actualPaidAmount.toFixed(2)}€ wurden von PayPal abgebucht. Dein Premium-Account ist jetzt aktiv!`,
          duration: 8000,
        });
        
                 // Subscription Status aktualisieren
         await fetchSubscription();
        
        // Modal schließen
        setIsProcessing(false);
        onClose();
        return;
        
      } catch (backendError) {
        console.error('❌ Backend capture failed:', backendError);
        
        // Fallback: KEINE lokale Verarbeitung mehr!
        // Das Geld wurde möglicherweise nicht abgebucht
        console.log('🔄 Backend capture failed, starting verification process...');
        toast.warning('Zahlung wird überprüft...', {
          description: 'Die PayPal-Zahlung wird überprüft. Falls bereits bezahlt, wird dein Account automatisch aktiviert.',
          duration: 8000,
        });
      }
      
      // Starte automatische Verifikation mit verbesserter Logik
      setIsVerifyingPayment(true);
      setPaymentVerificationFailed(false);
      setVerificationAttempts(0);
      setIsProcessing(false);
      
      // Längere Verzögerung für Webhook-Verarbeitung
      setTimeout(async () => {
        const success = await verifyPayment(data.orderID);
        setIsVerifyingPayment(false);
        
        if (!success) {
          console.log('Automatic verification failed, showing manual option');
          setPaymentVerificationFailed(true);
        }
      }, 3000); // Erhöht auf 3000ms für Webhook-Verarbeitung
      
    } catch (error) {
      console.error('Error processing PayPal approval:', error);
      
      // Spezifische Fehlerbehandlung für verschiedene Fehlertypen
      if (error.message?.includes('Window closed') || 
          error.message?.includes('XMLHttpRequest') ||
          error.message?.includes('CORS') ||
          error.message?.includes('capture')) {
        console.log('PayPal capture/CORS error - but payment might still be processed');
        
        // Bei Capture/CORS-Fehlern trotzdem versuchen zu verifizieren
        // PayPal captured oft trotzdem im Hintergrund
        if (data.orderID) {
          setLastOrderId(data.orderID);
          setIsVerifyingPayment(true);
          setPaymentVerificationFailed(false);
          setVerificationAttempts(0);
          setIsProcessing(false);
          
          // Längere Verzögerung bei Capture-Fehlern
          setTimeout(async () => {
            const success = await verifyPayment(data.orderID);
            setIsVerifyingPayment(false);
            
            if (!success) {
              setPaymentVerificationFailed(true);
            }
          }, 5000); // 5 Sekunden bei Capture-Problemen
          
          toast.info('Zahlung wird verarbeitet...', {
            description: 'Deine PayPal-Zahlung wird verarbeitet. Dies kann einen Moment dauern.',
            duration: 8000,
          });
        } else {
          toast.error('PayPal-Verbindungsfehler');
          setShowFallback(true);
          setIsProcessing(false);
          setIsVerifyingPayment(false);
        }
      } else {
        toast.error('Fehler beim Verarbeiten der Zahlung');
        setShowFallback(true);
        setIsProcessing(false);
        setIsVerifyingPayment(false);
      }
    }
  }, [user?.id]);

  const handlePayPalError = useCallback((err) => {
    console.error('PayPal error:', err);
    
    // Spezifische Fehlerbehandlung für verschiedene Fehlertypen
    if (err.message?.includes('Content Security Policy') || 
        err.message?.includes('unsafe-eval') ||
        err.message?.includes('postMessage')) {
      toast.error('PayPal-Sicherheitsfehler', {
        description: 'Verwende die Banküberweisung als Alternative.',
        duration: 6000,
      });
    } else if (err.message?.includes('Window closed') ||
               err.message?.includes('XMLHttpRequest') ||
               err.message?.includes('CORS')) {
      toast.warning('PayPal-Verbindungsproblem', {
        description: 'Falls du bereits bezahlt hast, verwende den "Bezahlung überprüfen" Button.',
        duration: 8000,
      });
    } else if (err.message?.includes('timeout') || 
               err.message?.includes('network')) {
      toast.error('PayPal-Netzwerkfehler', {
        description: 'Bitte überprüfe deine Internetverbindung und versuche es erneut.',
        duration: 6000,
      });
    } else {
      toast.error('PayPal-Fehler aufgetreten', {
        description: 'Verwende die Banküberweisung als Alternative.',
        duration: 6000,
      });
    }
    
    setShowFallback(true);
    setIsProcessing(false);
    setIsVerifyingPayment(false);
  }, []);

  // Erweiterte PayPal-Timeout-Behandlung
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (isPending || isLoading) {
      // Kürzerer Timeout für bessere UX
      timeoutId = setTimeout(() => {
        console.warn('PayPal loading timeout - showing fallback');
        setShowFallback(true);
        toast.warning('PayPal lädt zu lange. Alternative Zahlungsmethode verfügbar.');
      }, 8000); // 8 Sekunden statt 10
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isPending, isLoading]);

  // Reset PayPal state when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowFallback(false);
      setIsProcessing(false);
      
      // Force PayPal reload if it's in error state
      if (isRejected) {
        dispatch({
          type: 'resetOptions',
          value: {
            'client-id': getPayPalEnvironment().clientId,
            currency: 'EUR',
            intent: 'capture',
            components: 'buttons'
          }
        });
      }
    }
  }, [isOpen, isRejected, dispatch]);

  const handleSubscriptionApprove = async (planId: string, orderId: string) => {
    try {
      setIsProcessing(true);
      
      // Berechne das Startdatum für das Upgrade
      let upgradeStartDate = new Date();
      let periodEndDate = new Date();
      
      // Wenn Benutzer in Trial ist, beginne Upgrade nach Trial-Ende
      if (subscription?.tier === 'trial' && subscription?.trial_end) {
        upgradeStartDate = new Date(subscription.trial_end);
        periodEndDate = new Date(upgradeStartDate.getTime() + 365 * 24 * 60 * 60 * 1000);
      } else {
        // Sofortiges Upgrade für Free-Benutzer
        periodEndDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);
      }

      // Gutscheincode-Nutzung verfolgen
      if (appliedCoupon && user) {
        // Gutscheincode-Nutzung aktualisieren
        await supabase
          .from('coupon_codes')
          .update({ current_uses: appliedCoupon.current_uses + 1 })
          .eq('id', appliedCoupon.id);

        // User-spezifische Nutzung speichern
        await supabase
          .from('coupon_usage')
          .insert({
            user_id: user.id,
            coupon_id: appliedCoupon.id
          });
      }
      
      const { error } = await supabase
        .from('subscriptions')
        .update({
          tier: 'yearly',
          status: 'active',
          paypal_subscription_id: orderId,
          current_period_end: periodEndDate.toISOString(),
          premium_end_date: periodEndDate.toISOString(),
          cancel_at_period_end: false,
          // Trial-Ende beibehalten, wenn in Trial
          trial_end: subscription?.tier === 'trial' ? subscription.trial_end : null,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user?.id);

      if (error) throw error;
      
      await fetchSubscription();
      
      if (subscription?.tier === 'trial') {
        toast.success('Upgrade erfolgreich! Dein Premium-Abonnement beginnt nach dem Ende der Testphase.');
      } else {
        toast.success('Abonnement erfolgreich aktiviert!');
      }
      
      onClose();
    } catch (error) {
      console.error('Error updating subscription:', error);
      toast.error('Fehler beim Aktivieren des Abonnements');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFreeActivation = async () => {
    if (!appliedCoupon || finalYearlyPrice > 0) return;

    try {
      setIsProcessing(true);

      // Gutscheincode-Nutzung verfolgen
      if (user) {
        // Gutscheincode-Nutzung aktualisieren
        await supabase
          .from('coupon_codes')
          .update({ current_uses: appliedCoupon.current_uses + 1 })
          .eq('id', appliedCoupon.id);

        // User-spezifische Nutzung speichern
        await supabase
          .from('coupon_usage')
          .insert({
            user_id: user.id,
            coupon_id: appliedCoupon.id
          });
      }

      const periodEndDate = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000);

      const { error } = await supabase
        .from('subscriptions')
        .update({
          tier: 'yearly',
          status: 'active',
          current_period_end: periodEndDate.toISOString(),
          premium_end_date: periodEndDate.toISOString(),
          cancel_at_period_end: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user?.id);

      if (error) throw error;
      
      await fetchSubscription();
      toast.success('Kostenfreies Abonnement erfolgreich aktiviert!');
      onClose();
    } catch (error) {
      console.error('Error activating free subscription:', error);
      toast.error('Fehler beim Aktivieren des Abonnements');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleOneTimePayment = async (orderId: string) => {
    if (!user?.id) {
      console.error('User not authenticated in handleOneTimePayment');
      throw new Error('Benutzer nicht angemeldet');
    }

    console.log('Processing one-time payment for order:', orderId);
    
    try {
      const periodEndDate = new Date();
      periodEndDate.setFullYear(periodEndDate.getFullYear() + 1);

      // Gutscheincode-Nutzung aktualisieren falls angewendet
      if (appliedCoupon) {
        // Gutscheincode-Nutzung aktualisieren
        await supabase
          .from('coupon_codes')
          .update({ current_uses: appliedCoupon.current_uses + 1 })
          .eq('id', appliedCoupon.id);

        // User-spezifische Nutzung speichern
        await supabase
          .from('coupon_usage')
          .insert({
            user_id: user.id,
            coupon_id: appliedCoupon.id
          });
      }
      
      // Premium-Abonnement sofort aktivieren mit verbesserter Datenstruktur
      const { error } = await supabase
        .from('subscriptions')
        .update({
          tier: 'yearly',
          status: 'active',
          paypal_subscription_id: orderId,
          payment_id: orderId, // Zusätzlich als payment_id speichern
          payment_provider: 'paypal', // Provider explizit setzen
          premium_end_date: periodEndDate.toISOString(),
          current_period_end: periodEndDate.toISOString(),
          current_period_start: new Date().toISOString(),
          cancel_at_period_end: false,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;
      
      // Verbesserte Webhook-Log-Erstellung
      await supabase
        .from('webhook_logs')
        .insert({
          event_type: 'PAYMENT.PROCESSED.LOCALLY',
          payload: {
            order_id: orderId,
            user_id: user.id,
            amount: finalYearlyPrice,
            currency: 'EUR',
            processed_at: new Date().toISOString(),
            payment_provider: 'paypal',
            local_processing: true,
            coupon_applied: appliedCoupon ? {
              code: appliedCoupon.code,
              discount_type: appliedCoupon.discount_type,
              discount_value: appliedCoupon.discount_value
            } : null
          },
          success: true,
          error_message: null,
          created_at: new Date().toISOString()
        });
      
      await fetchSubscription();
      
      console.log('Local payment processing completed successfully');
      
    } catch (error) {
      console.error('Error in handleOneTimePayment:', error);
      
      // Webhook-Log für Fehler erstellen
      try {
        await supabase
          .from('webhook_logs')
          .insert({
            event_type: 'PAYMENT.PROCESSING.ERROR',
            payload: {
              order_id: orderId,
              user_id: user?.id,
              error_message: error.message,
              processed_at: new Date().toISOString()
            },
            success: false,
            error_message: error.message,
            created_at: new Date().toISOString()
          });
      } catch (logError) {
        console.warn('Failed to log payment error:', logError);
      }
      
      throw error; // Re-throw für bessere Fehlerbehandlung
    }
  };

  const handlePayPalCancel = () => {
    toast.info('Zahlung abgebrochen');
  };

  // Stripe-Zahlungsverarbeitung mit dynamischem Preis
  const handleStripePayment = async () => {
    if (!user?.id) {
      toast.error('Bitte melde dich an, um fortzufahren');
      return;
    }

    setIsStripeProcessing(true);
    
    try {
      console.log('🔄 Creating Stripe payment...');
      
      // finalYearlyPrice enthält bereits den korrekten Preis nach Gutschein!
      const originalAmountCents = 1690; // 16.90€ = 1690 Cent (ursprünglicher Preis)
      const finalAmountCents = Math.round(finalYearlyPrice * 100); // Bereits korrekter Preis nach Gutschein
      
      console.log('💰 Stripe payment details:', {
        originalAmount: originalAmountCents / 100,
        finalAmount: finalAmountCents / 100,
        coupon: appliedCoupon?.code || 'none',
        discount: appliedCoupon ? `${appliedCoupon.discount_value}${appliedCoupon.discount_type === 'percentage' ? '%' : '€'}` : 'none'
      });
      
      // Erstelle Stripe Checkout Session über Edge Function
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        },
        body: JSON.stringify({
          userID: user.id,
          amount: finalAmountCents, // Finaler Preis nach Gutschein in Cent
          originalAmount: originalAmountCents, // Ursprünglicher Preis in Cent
          currency: 'EUR',
          couponCode: appliedCoupon?.code
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`Stripe payment creation failed: ${response.status} - ${JSON.stringify(errorData)}`);
      }

      const result = await response.json();
      
      // Prüfe ob kostenfreie Aktivierung erfolgte
      if (result.freeActivation) {
        console.log('✅ Free premium activation due to high discount');
        
        const finalPrice = finalAmountCents / 100;
        toast.success('🎉 Premium kostenfrei aktiviert!', {
          description: `Dank deines Gutscheins (${finalPrice.toFixed(2)}€) wurde dein Premium-Account kostenfrei freigeschaltet!`,
          duration: 8000,
        });
        
        // Konfetti-Effekt
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        
        // Subscription Status aktualisieren
        await fetchSubscription();
        
        // Modal schließen
        setIsStripeProcessing(false);
        onClose();
        return;
      }
      
      if (!result.url) {
        throw new Error('No payment URL received from Stripe');
      }
      
      console.log('✅ Stripe checkout session created:', {
        url: result.url,
        sessionId: result.sessionId,
        amount: result.amount / 100,
        paymentType: result.paymentType
      });
      
      // Mobile-optimierte Weiterleitung
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (isMobile) {
        // Auf Mobile: Direkte Weiterleitung
        console.log('📱 Mobile detected - redirecting directly');
        window.location.href = result.url;
      } else {
        // Auf Desktop: Neuer Tab
        console.log('💻 Desktop detected - opening new tab');
        const newWindow = window.open(result.url, '_blank', 'noopener,noreferrer');
        if (!newWindow) {
          // Fallback wenn Popup blockiert wurde
          window.location.href = result.url;
        }
      }
      
      const finalPrice = finalAmountCents / 100;
      toast.success('💳 Stripe-Zahlung geöffnet', {
        description: `Einmalige Zahlung über ${finalPrice.toFixed(2)}€ ${isMobile ? 'wird geladen' : 'wurde in einem neuen Tab geöffnet'}.`,
        duration: 5000,
      });
      
    } catch (error) {
      console.error('Stripe payment error:', error);
      toast.error('Fehler bei Stripe-Zahlung', {
        description: 'Die Stripe-Zahlung konnte nicht gestartet werden.',
        duration: 5000,
      });
    } finally {
      setIsStripeProcessing(false);
    }
  };

  const renderUpgradeButton = () => {
    // Kostenfreies Abo wenn Preis 0
    if (finalYearlyPrice === 0 && appliedCoupon?.applicable_plans.includes('yearly')) {
      return (
        <Button
          onClick={handleFreeActivation}
          disabled={isProcessing}
          className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white h-11 text-sm rounded-xl"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Wird aktiviert...
            </>
          ) : (
            'Kostenfreies Abo aktivieren'
          )}
        </Button>
      );
    }

    // Zeige nur PayPal-Fehler ohne manuelle Bestellung
    if (showFallback || isRejected) {
      return (
        <div className="space-y-3">
          <div className="text-center p-4 bg-red-50 border border-red-200 rounded-xl">
            <div className="text-red-800 text-sm font-medium mb-2">
              ⚠️ PayPal-Ladevorgang fehlgeschlagen
            </div>
            <div className="text-red-700 text-sm">
              PayPal konnte nicht geladen werden. Bitte versuche es erneut oder verwende die Banküberweisung.
            </div>
          </div>
          
          <Button 
            onClick={() => {
              setShowFallback(false);
              window.location.reload();
            }} 
            variant="outline"
            className="w-full h-10 text-sm rounded-xl"
          >
            PayPal erneut versuchen
          </Button>
        </div>
      );
    }

    // Loading-Zustand
    if (isPending || isLoading || isInitial || isProcessing) {
      return (
        <div className="text-center">
          <div className="flex justify-center py-1">
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
          <p className="text-xs text-gray-500">
            {isInitial && 'PayPal wird initialisiert...'}
            {isLoading && 'PayPal wird geladen...'}
            {isPending && 'PayPal ist bereit...'}
            {isProcessing && 'Zahlung wird verarbeitet...'}
          </p>
        </div>
      );
    }

    if (!isPayPalConfigured()) {
      const config = getPayPalEnvironment();
      
      return (
        <div className="text-center p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
          <div className="text-yellow-800 text-sm font-medium mb-2">
            🚧 PayPal nicht konfiguriert
          </div>
          <div className="text-yellow-700 text-sm">
            <div>Client-ID: {config.clientId ? 
              `Verfügbar (${config.clientId.slice(0, 8)}...)` : 'Fehlt'}</div>
            <div>Umgebung: {config.environment}</div>
          </div>
        </div>
      );
    }

    // PayPal ist resolved - zeige Zahlungsmethoden-Auswahl
    if (isResolved) {
      const paypalKey = `paypal-yearly-${finalYearlyPrice}-${appliedCoupon?.id || 'none'}`;

      return (
        <div className="space-y-3">
          {/* Zahlungsmethoden-Auswahl */}
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl">
            <button
              onClick={() => setSelectedPaymentMethod('paypal')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                selectedPaymentMethod === 'paypal'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              PayPal
            </button>
            <button
              onClick={() => setSelectedPaymentMethod('stripe')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                selectedPaymentMethod === 'stripe'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Stripe
            </button>
            <button
              onClick={() => setSelectedPaymentMethod('bank')}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                selectedPaymentMethod === 'bank'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Überweisung
            </button>
          </div>

          {/* PayPal Buttons */}
          {selectedPaymentMethod === 'paypal' && (
            <div className="min-h-[44px]">
              <PayPalButtons
                key={paypalKey}
                createOrder={handlePayPalCreateOrder}
                onApprove={handlePayPalApprove}
                onCancel={handlePayPalCancel}
                onError={handlePayPalError}
                style={{ 
                  layout: 'horizontal',
                  color: 'gold',
                  shape: 'rect',
                  label: 'pay',
                  height: 44,
                  tagline: false
                }}
                disabled={isProcessing || isVerifyingPayment}
                forceReRender={[finalYearlyPrice, user?.id]}
              />
            </div>
          )}

          {/* Stripe Button */}
          {selectedPaymentMethod === 'stripe' && (
            <Button
              onClick={handleStripePayment}
              disabled={isStripeProcessing || isProcessing}
              className="w-full bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white h-11 text-sm rounded-xl"
            >
              {isStripeProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Stripe wird geladen...
                </>
              ) : (
                <>
                  {(() => {
                    // finalYearlyPrice enthält bereits den korrekten Preis nach Gutschein!
                    const finalAmountCents = Math.round(finalYearlyPrice * 100);
                    
                    // Wenn unter 0,50€, zeige "Kostenfrei aktivieren"
                    if (finalAmountCents < 50) {
                      return `🎉 Kostenfrei aktivieren (${finalYearlyPrice.toFixed(2)}€)`;
                    }
                    
                    return `💳 Mit Stripe bezahlen (${finalYearlyPrice.toFixed(2)}€)`;
                  })()}
                </>
              )}
            </Button>
          )}

          {/* Banküberweisung */}
          {selectedPaymentMethod === 'bank' && (
            <div className="space-y-4">
              {/* Bankdaten-Karte */}
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 border border-blue-200/60">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-medium text-blue-900 flex items-center gap-2">
                    <Building2 className="w-5 h-5" />
                    Bankdaten für Überweisung
                  </CardTitle>
                  <CardDescription className="text-blue-700">
                    Überweise {finalYearlyPrice.toFixed(2)}€ an folgende Bankverbindung:
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* IBAN */}
                  <div className="bg-white/70 rounded-lg p-3 border border-blue-200/40">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs font-medium text-blue-700">IBAN</Label>
                        <p className="font-mono text-sm font-medium text-gray-900">DE32 1001 1001 2627 6461 09</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText('DE32100110012627646109');
                          toast.success('IBAN kopiert!');
                        }}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-100 h-8 w-8 p-0 rounded-lg"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* BIC */}
                  <div className="bg-white/70 rounded-lg p-3 border border-blue-200/40">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs font-medium text-blue-700">BIC</Label>
                        <p className="font-mono text-sm font-medium text-gray-900">NTSBDEB1XXX</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText('NTSBDEB1XXX');
                          toast.success('BIC kopiert!');
                        }}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-100 h-8 w-8 p-0 rounded-lg"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Empfänger */}
                  <div className="bg-white/70 rounded-lg p-3 border border-blue-200/40">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs font-medium text-blue-700">Empfänger</Label>
                        <p className="text-sm font-medium text-gray-900">Tobias Poczesniok</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText('Tobias Poczesniok');
                          toast.success('Empfänger kopiert!');
                        }}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-100 h-8 w-8 p-0 rounded-lg"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Verwendungszweck */}
                  <div className="bg-white/70 rounded-lg p-3 border border-blue-200/40">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <Label className="text-xs font-medium text-blue-700">Verwendungszweck</Label>
                        <p className="font-mono text-sm font-medium text-gray-900 break-all">
                          {generateTransferReference()}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          ⚠️ Bitte exakt so angeben für automatische Zuordnung
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(generateTransferReference());
                          toast.success('Verwendungszweck kopiert!');
                        }}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-100 h-8 w-8 p-0 rounded-lg flex-shrink-0"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Betrag */}
                  <div className="bg-white/70 rounded-lg p-3 border border-blue-200/40">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-xs font-medium text-blue-700">Betrag</Label>
                        <p className="text-lg font-semibold text-emerald-700">{finalYearlyPrice.toFixed(2)}€</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(finalYearlyPrice.toFixed(2));
                          toast.success('Betrag kopiert!');
                        }}
                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-100 h-8 w-8 p-0 rounded-lg"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Hinweise */}
              <div className="bg-amber-50/50 border border-amber-200/60 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Clock className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium text-amber-900 text-sm">Wichtige Hinweise</h4>
                    <ul className="text-sm text-amber-800 space-y-1">
                      <li>• Dein Premium-Account wird nach Zahlungseingang freigeschaltet</li>
                      <li>• Die Bearbeitung dauert in der Regel 1-2 Werktage</li>
                      <li>• Verwende unbedingt den angegebenen Verwendungszweck</li>
                      <li>• Du erhältst eine E-Mail-Bestätigung nach Aktivierung</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Bestätigung Button */}
              <Button
                onClick={handleBankTransferOrder}
                disabled={isProcessing}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white h-11 text-sm rounded-xl"
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Wird verarbeitet...
                  </>
                ) : (
                  <>
                    🏦 Überweisung bestätigen
                  </>
                )}
              </Button>
            </div>
          )}


        </div>
      );
    }

    // Fallback wenn kein Status zutrifft
    return (
      <div className="text-center p-2 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="text-gray-800 text-sm font-medium mb-1">
          🤔 Unbekannter PayPal-Status
        </div>
        <div className="text-gray-600 text-xs mb-2">
          Bitte lade die Seite neu oder verwende die Banküberweisung.
        </div>
        <Button 
          onClick={() => window.location.reload()}
          variant="outline"
          size="sm"
          className="h-7 text-xs"
        >
          Seite neu laden
        </Button>
      </div>
    );
  };

  // Generiere eine eindeutige Verwendungszweck-ID
  const generateTransferReference = () => {
    const userPrefix = user?.email?.substring(0, 3).toUpperCase() || 'USR';
    const userId = user?.id?.substring(0, 8) || 'UNKNOWN';
    const timestamp = Date.now().toString().slice(-6);
    return `KP-${userPrefix}-${userId}-${timestamp}`;
  };

  const handleBankTransferOrder = async () => {
    try {
      setIsProcessing(true);
      const transferReference = generateTransferReference();
      
      // Speichere Bank Transfer Order in Supabase für Admin-Tracking
      const { error: orderError } = await supabase
        .from('bank_transfer_orders')
        .insert({
          user_id: user?.id,
          user_email: user?.email,
          transfer_reference: transferReference,
          amount: finalYearlyPrice,
          coupon_code: appliedCoupon?.code || null,
          status: 'pending',
          created_at: new Date().toISOString()
        });

      if (orderError) {
        console.error('Error creating bank transfer order:', orderError);
        // Trotzdem fortfahren, auch wenn das Logging fehlschlägt
      }

      // Gutscheincode-Nutzung verfolgen
      if (appliedCoupon && user) {
        await supabase
          .from('coupon_codes')
          .update({ current_uses: appliedCoupon.current_uses + 1 })
          .eq('id', appliedCoupon.id);

        await supabase
          .from('coupon_usage')
          .insert({
            user_id: user.id,
            coupon_id: appliedCoupon.id
          });
      }

      setShowBankTransferModal(false);
      onClose();
      toast.success('Bankdaten bereit!', {
        description: `Verwendungszweck: ${transferReference}. Dein Account wird nach Zahlungseingang freigeschaltet.`,
        duration: 8000,
      });
    } catch (error) {
      console.error('Error processing bank transfer order:', error);
      toast.error('Fehler beim Verarbeiten der Bestellung');
    } finally {
      setIsProcessing(false);
    }
  };

  // Kontaktformular für Support
  const sendSupportMessage = async () => {
    if (!user?.id || !lastOrderId) {
      toast.error('Fehlende Informationen für Support-Anfrage');
      return;
    }
    
    try {
      setIsProcessing(true);
      
      const formData = new FormData();
      formData.append('name', user.email || 'Unbekannt');
      formData.append('email', user.email || '');
      formData.append('subject', 'PayPal Zahlungsproblem - KaufPause Premium');
      formData.append('message', `
Hallo,

ich habe ein Problem mit meiner PayPal-Zahlung für KaufPause Premium.

Benutzer-ID: ${user.id}
PayPal Order ID: ${lastOrderId}
E-Mail: ${user.email || 'Nicht verfügbar'}
Zeitpunkt: ${new Date().toLocaleString('de-DE')}
Betrag: ${finalYearlyPrice.toFixed(2)}€

Die Zahlung wurde über PayPal abgewickelt, aber mein Account wurde nicht auf Premium freigeschaltet.

Bitte prüfen Sie die Zahlung und schalten Sie meinen Account manuell frei.

Vielen Dank!
      `);
      
      // Formspree Endpoint - konfiguriert über Umgebungsvariable
      const formspreeUrl = import.meta.env.VITE_FORMSPREE_URL || 'https://formspree.io/f/YOUR_FORMSPREE_ID';
      const response = await fetch(formspreeUrl, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.ok) {
        toast.success('Support-Anfrage gesendet!', {
          description: 'Wir werden uns innerhalb von 24 Stunden bei dir melden.',
          duration: 6000,
        });
        setShowContactForm(false);
      } else {
        throw new Error('Formspree request failed');
      }
      
    } catch (error) {
      console.error('Error sending support message:', error);
      toast.error('Fehler beim Senden der Support-Anfrage', {
        description: 'Bitte kontaktiere uns direkt per E-Mail.',
        duration: 6000,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Render-Logik für Verifikations-UI
  const renderVerificationUI = () => {
    if (isVerifyingPayment) {
      return (
        <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl shadow-sm">
          <div className="flex items-center gap-4 mb-4">
            <div className="relative">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              <div className="absolute inset-0 w-6 h-6 border-2 border-blue-200 rounded-full animate-pulse"></div>
            </div>
            <div>
              <span className="text-base font-semibold text-blue-900 block">
                Zahlung wird verifiziert...
              </span>
              <span className="text-sm text-blue-700">
                Versuch {verificationAttempts + 1} von 5
              </span>
            </div>
          </div>
          
          <p className="text-sm text-blue-700 mb-4 leading-relaxed">
            🔍 Wir prüfen deine PayPal-Zahlung und schalten dein Premium-Account automatisch frei.
            Dies kann bis zu 15 Sekunden dauern.
          </p>
          
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-blue-600 font-medium">
              <span>Fortschritt</span>
              <span>{Math.round(((verificationAttempts + 1) / 5) * 100)}%</span>
            </div>
            <div className="bg-blue-100 rounded-full h-3 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-700 ease-out relative"
                style={{ width: `${((verificationAttempts + 1) / 5) * 100}%` }}
              >
                <div className="absolute inset-0 bg-white/20 animate-pulse rounded-full"></div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (paymentVerificationFailed && lastOrderId) {
      return (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <span className="text-sm font-medium text-amber-900">
              Automatische Verifikation fehlgeschlagen
            </span>
          </div>
          <p className="text-xs text-amber-700 mb-3">
            Deine Zahlung wurde möglicherweise verarbeitet, aber die automatische Freischaltung hat nicht funktioniert.
          </p>
          <div className="flex gap-2">
            <Button
              onClick={handleManualVerification}
              disabled={isManualVerifying}
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              {isManualVerifying ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Prüfe...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Bezahlung überprüfen
                </>
              )}
            </Button>
            <Button
              onClick={() => setShowContactForm(true)}
              variant="outline"
              size="sm"
            >
              <Mail className="w-4 h-4 mr-2" />
              Support kontaktieren
            </Button>
          </div>
          <div className="mt-2 text-xs text-amber-600">
            Order-ID: <code className="bg-amber-100 px-1 rounded">{lastOrderId}</code>
          </div>
        </div>
      );
    }
    
    return null;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[520px] max-h-[90vh] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl p-4">
        <DialogHeader className="text-center pb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl flex items-center justify-center mx-auto mb-2">
            <Crown className="w-6 h-6 text-white" />
          </div>
          <DialogTitle className="text-xl font-semibold text-gray-900">Premium freischalten</DialogTitle>
        </DialogHeader>

        {/* Hauptvorteil */}
        <div className="bg-gradient-to-r from-emerald-50 to-emerald-100/50 p-3 rounded-xl border border-emerald-200/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-emerald-900">Unbegrenzte Produkte</h3>
              <p className="text-sm text-emerald-700">Füge so viele Produkte hinzu, wie du möchtest</p>
            </div>
          </div>
        </div>

        {/* Wichtige Hinweise */}
        <div>
          <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200/60 rounded-xl">
            <Shield className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <div>
              <span className="font-semibold text-blue-900 text-sm">Keine automatische Verlängerung</span>
              <p className="text-blue-700 text-sm">Einmalige Zahlung - keine Abo-Falle</p>
            </div>
          </div>
        </div>

        {/* Gutscheincode-Bereich - jetzt volle Breite */}
        <div className="space-y-2">
          {/* Gutscheincode Toggle Button */}
          <Button
            variant="outline"
            onClick={() => setShowCouponField(!showCouponField)}
            className="w-full h-12 border-gray-300 hover:border-gray-400 rounded-xl flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              <span className="text-sm">Gutscheincode</span>
            </div>
            <div className={`transform transition-transform ${showCouponField ? 'rotate-180' : ''}`}>
              ▼
            </div>
          </Button>

          {/* Ausklappbares Gutscheinfeld */}
          {showCouponField && (
            <div className="space-y-2">
              <div className="relative">
                <Input
                  id="coupon"
                  placeholder="z.B. WELCOME"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="bg-white border-gray-300 focus:border-emerald-400 focus:ring-emerald-300/50 rounded-xl pr-10 h-10 text-sm placeholder:text-gray-400"
                />
                {isValidatingCoupon && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
              
              {/* Feedback-Nachrichten */}
              {couponError && !isValidatingCoupon && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {couponError}
                </p>
              )}
              {appliedCoupon && !isValidatingCoupon && !couponError && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Gutschein angewendet: {appliedCoupon.discount_type === 'percentage' 
                    ? `${appliedCoupon.discount_value}% Rabatt` 
                    : `${appliedCoupon.discount_value}€ Rabatt`}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Jahres-Lizenz */}
        <Card className="relative overflow-hidden border-2 border-emerald-500 rounded-xl">
          <div className="absolute top-0 right-0 bg-emerald-500 text-white px-3 py-1 rounded-bl-xl text-sm font-medium">
            Beste Wahl
          </div>
          <CardHeader className="pb-1 pt-3">
            <CardTitle className="text-lg font-semibold text-gray-900">Jahres-Lizenz</CardTitle>
          </CardHeader>
          <CardContent className="pt-0 pb-3">
            {/* Preis-Container */}
            <div className="flex items-center mb-1">
              {finalYearlyPrice === 0 && appliedCoupon?.applicable_plans.includes('yearly') ? (
                <span className="text-2xl font-medium text-emerald-600">Kostenlos</span>
              ) : (
                <div className="text-2xl font-medium text-gray-900">
                  {Math.floor(finalYearlyPrice)}<span className="text-lg">,{(finalYearlyPrice % 1).toFixed(2).substring(2)}</span>€
                  <span className="text-sm font-normal text-gray-500">/Jahr</span>
                </div>
              )}
            </div>
            {/* Ursprünglicher Preis */}
            {appliedCoupon?.applicable_plans.includes('yearly') && 16.90 !== finalYearlyPrice && (
              <div className="text-sm text-gray-500 line-through mb-3">
                {Math.floor(16.90)},{(16.90 % 1).toFixed(2).substring(2)}€/Jahr
              </div>
            )}
            
            {/* Zahlungsverifikation anzeigen */}
            {renderVerificationUI()}
            
            {/* Button-Container */}
            <div className="min-h-[44px] flex flex-col justify-end">
              {!isVerifyingPayment && !paymentVerificationFailed && renderUpgradeButton()}
            </div>
            

          </CardContent>
        </Card>

        <DialogFooter className="pt-4">
          <Button 
            variant="ghost" 
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 h-10 text-sm"
          >
            Später
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 